import * as React from "react";

import { Text, styled } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import {
  BasePicker,
  IBasePickerStyleProps,
  IBasePickerStyles,
  IBasePickerSuggestionsProps,
  IPickerItemProps,
  TagItemSuggestion,
} from "@fluentui/react/lib/Pickers";
import { getTheme, mergeStyles } from "@fluentui/react/lib/Styling";
import { getStyles } from "@fluentui/react/lib/components/pickers/BasePicker.styles";
import { BuildingItem } from "./BuildingItem";
import { IBuilding, IBuildingPickerProps } from "./BuildingPicker.types";

const theme = getTheme();
const rootClass = mergeStyles({
  width: "100%",
  marginBottom: 10,
});

const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
  resultsMaximumNumber: 100, // Same as REC BuildlingSelect directive
  noResultsFoundText: "Keine Objekte mit dieser Suche gefunden",
  loadingText: "Lade Objekte...",
  className: mergeStyles({
    minWidth: 250,
    maxWidth: 500,
  }),
};

export class BuildingPickerBase extends BasePicker<IBuilding, IBuildingPickerProps> {
  public static defaultProps = {
    onRenderItem: (props: IPickerItemProps<IBuilding>) => (
      <BuildingItem
        {...props}
        key={props.item.id}
        styles={{
          root: { maxWidth: "unset" },
        }}
      >
        {props.item.developerAcronym} {props.item.number} | {props.item.address}
      </BuildingItem>
    ),
    onRenderSuggestionsItem: (building: IBuilding) => (
      <span key={building.id} title={building.address}>
        <TagItemSuggestion
          styles={{
            suggestionTextOverflow: {
              textAlign: "left",
              maxWidth: "inherit",
              // displays a grey line under the suggestion item to seperate them
              "&::after": {
                display: "block",
                background: "#d4d4d4",
                content: '""',
                height: "1px",
                position: "absolute",
                bottom: 0,
                left: 8,
                right: 8,
              },
            },
          }}
        >
          <strong>{building.address}</strong>
          <br />
          <small>
            {building.developerAcronym} {building.number}
            {building.unitsCount >= 0
              ? ` | ${building.unitsCount} ${building.unitsCount === 1 ? "Einheit" : "Einheiten"}`
              : undefined}
          </small>
        </TagItemSuggestion>
        {/* <div className={greyLineClass} aria-hidden={true}></div> */}
      </span>
    ),
  };
}

const Picker = styled<IBuildingPickerProps, IBasePickerStyleProps, IBasePickerStyles>(
  BuildingPickerBase,
  getStyles,
  undefined,
  {
    scope: "BuildingPicker",
  }
);

export const BuildingPicker: React.FunctionComponent<IBuildingPickerProps> = ({
  onChange,
  selectedItems,
  onResolveSuggestions,
}) => {
  const pickerId = useId("inline-picker");
  const picker = React.useRef(null);
  const isError = selectedItems.length <= 0;

  return (
    <div className={rootClass}>
      <label htmlFor={pickerId}>Objekt(e):</label>
      <Picker
        styles={{
          text: { borderColor: isError ? theme.palette.redDark : theme.palette.greenLight },
        }}
        selectedItems={selectedItems}
        removeButtonAriaLabel="Entfernen"
        selectionAriaLabel="Ausgewählte Objekte"
        getTextFromItem={(building: IBuilding) => building.address}
        pickerSuggestionsProps={pickerSuggestionsProps}
        resolveDelay={450}
        onEmptyResolveSuggestions={(selectedItems) => onResolveSuggestions(undefined, selectedItems)}
        onResolveSuggestions={onResolveSuggestions}
        onChange={onChange}
        onDismiss={() => {
          // clear input on dismiss
          picker.current?.input.current?._updateValue("");
          // override default behaviour - do not add the currently selected item
          return false;
        }}
        // this option tells the picker's callout to render inline instead of in a new layer
        pickerCalloutProps={{ doNotLayer: false }}
        componentRef={picker}
        inputProps={{
          id: pickerId,
        }}
      />
      <Text
        variant="small"
        styles={{
          root: {
            color: isError ? theme.palette.redDark : undefined,
            display: isError ? "block" : "none",
            paddingTop: 5,
          },
        }}
      >
        Sie müssen mindestens ein Objekt auswählen
      </Text>
    </div>
  );
};
